import type { Distance, SpatialRectangle } from "@pdfking/math"
import { Min, Scale, Subtract, Total } from "@pdfking/math"
import { MM } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { HorizontalAlign, PlotFunction } from "@pdfking/renderer-api"
import { Range } from "@pdfking/tfunc"

import { PALETTE } from "#constants"

import { PlainText } from "../text/PlainText"
import { DayColor } from "./DayColor"
import { WeekdayFont } from "./WeekdayFont"
import { WeekdayNames } from "./WeekdayNames"

// The rule under the strip
const STROKE = MM(0.3)

/** Month `month` of `year` across `box`, a day a column as a strip calendar
 * has it: its weekday's initial over its number, ruled under; Sundays
 * holidays (see DayColor); `ruled` under unless told; its initials in
 * `language`. */
export const DrawDayStrip = ({
  box,
  language,
  month,
  ruled = true,
  year,
}: {
  box: SpatialRectangle<Distance>
  language: string
  month: number
  ruled?: boolean
  year: number
}): PageShape[] => {
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const column = Scale(box.width, 1 / 31)
  const font = Min(Scale(box.height, 0.35), Scale(column, 0.6))
  const names = WeekdayNames(language)
  const middle = Total(box.y, Scale(box.height, 0.5))
  return [
    ...[...Range(1, days)].flatMap(day => {
      const weekday = (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7
      const color = DayColor(weekday)
      const x = Total(box.x, Scale(column, day - 1))
      return [
        PlainText(
          names[weekday]!.charAt(0).toUpperCase(),
          x,
          column,
          Total(middle, Scale(font, 0.2)),
          WeekdayFont(font),
          color,
          HorizontalAlign.CENTER
        ),
        PlainText(
          `${day}`,
          x,
          column,
          Subtract(middle, Scale(font, 1.2)),
          font,
          color,
          HorizontalAlign.CENTER
        ),
      ]
    }),
    ...(ruled
      ? [
          {
            enum: PlotFunction.PLOT_RECT,
            fill_color: PALETTE.secondary,
            position: { height: STROKE, width: box.width, x: box.x, y: box.y },
          } as PageShape,
        ]
      : []),
  ]
}
